import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment, useMemo } from "react";
import { findUsedGcmConsentTypes } from "@devowl-wp/cookie-consent-web-client";
import { yieldLazyLoad, yieldLazyLoadFromImport } from "@devowl-wp/react-utils";
import { extendBannerBodyStylesheet } from "@devowl-wp/web-cookie-banner";
import { BannerButtonList } from "./buttonList.js";
import { useBanner } from "../../../contexts/banner.js";
import { useBannerStylesheet } from "../../../hooks/banner/useBannerStylesheet.js";
import { useGroupItems } from "../../../hooks/common/useGroupItems.js";
import { useCommonTeachings } from "../../../hooks/common/useTeachings.js";
import { BodyDescription } from "../../common/body/description.js";
import { Group } from "../../common/groups/group.js";
import { BannerDottedGroupList } from "../groups/dottedGroupList.js";
const YieldBodyDescription = yieldLazyLoad(Promise.resolve(BodyDescription), "BodyDescription");
const BannerGroupList = yieldLazyLoadFromImport(import(/* webpackChunkName: "banner-lazy", webpackMode: "lazy-once" */ "../../banner/groups/list.js"), ({ BannerGroupList })=>BannerGroupList);
const BannerTcfGroupList = process.env.IS_TCF === "1" && yieldLazyLoadFromImport(import(/* webpackChunkName: "banner-lazy", webpackMode: "lazy-once" */ "../../banner/tcf/groups.js"), ({ BannerTcfGroupList })=>BannerTcfGroupList);
const BannerGcmGroupList = process.env.PLUGIN_CTX === "pro" && yieldLazyLoadFromImport(import(/* webpackChunkName: "banner-lazy", webpackMode: "lazy-once" */ "../../banner/gcm/groups.js"), ({ BannerGcmGroupList })=>BannerGcmGroupList);
const BannerHistorySelect = yieldLazyLoadFromImport(import(/* webpackChunkName: "banner-lazy", webpackMode: "lazy-once" */ "../../banner/body/historySelect.js"), ({ BannerHistorySelect })=>BannerHistorySelect);
const BANNER_GROUP_NON_STANDARD_STYLE = {
    margin: "20px 0 10px 0"
};
const BannerBody = ({ leftSideContainerRef, rightSideContainerRef })=>{
    const { a11yIds, Container, RightSide, LeftSide, BeforeFooter } = useBannerStylesheet().extend(...extendBannerBodyStylesheet);
    const banner = useBanner();
    const { tcf, isGcm, individualPrivacyOpen, activeAction, individualTexts: { postamble }, i18n: { nonStandard, nonStandardDesc }, groups, designVersion } = banner;
    const services = useGroupItems();
    const teachings = useCommonTeachings({
        services,
        disableListServicesNotice: designVersion > 9 ? individualPrivacyOpen : false
    });
    const hasUsedGcmConsentTypes = isGcm && findUsedGcmConsentTypes(services).length > 0;
    const hasMoreThanOneStandard = useMemo(()=>!!(process.env.IS_TCF === "1" && tcf || process.env.PLUGIN_CTX === "pro" && hasUsedGcmConsentTypes), [
        tcf,
        hasUsedGcmConsentTypes
    ]);
    const leftSideContainer = /*#__PURE__*/ _jsxs(LeftSide, {
        ref: leftSideContainerRef,
        children: [
            /*#__PURE__*/ _jsx(YieldBodyDescription, {
                id: a11yIds.description,
                ...groups.length > 0 ? teachings : {},
                children: activeAction === "history" && /*#__PURE__*/ _jsx(BannerHistorySelect, {})
            }),
            groups.length > 0 && /*#__PURE__*/ _jsxs(Fragment, {
                children: [
                    individualPrivacyOpen ? /*#__PURE__*/ _jsxs(Fragment, {
                        children: [
                            hasMoreThanOneStandard && /*#__PURE__*/ _jsx(Group, {
                                headline: nonStandard,
                                style: BANNER_GROUP_NON_STANDARD_STYLE,
                                borderless: true,
                                children: nonStandardDesc
                            }),
                            /*#__PURE__*/ _jsx(BannerGroupList, {}),
                            [
                                process.env.IS_TCF === "1" && tcf && /*#__PURE__*/ _jsx(BannerTcfGroupList, {}, "tcf"),
                                process.env.PLUGIN_CTX === "pro" && hasUsedGcmConsentTypes && /*#__PURE__*/ _jsx(BannerGcmGroupList, {}, "gcm")
                            ].filter(Boolean).sort(()=>designVersion < 10 ? 1 : -1)
                        ]
                    }) : /*#__PURE__*/ _jsx(BannerDottedGroupList, {}),
                    !!postamble && individualPrivacyOpen && /*#__PURE__*/ _jsx(YieldBodyDescription, {
                        teachings: [
                            postamble
                        ],
                        isPostamble: true
                    })
                ]
            })
        ]
    }, "leftSide");
    const rightSideContainer = activeAction === "history" ? /*#__PURE__*/ _jsx("div", {
        ref: rightSideContainerRef
    }) : /*#__PURE__*/ _jsx(RightSide, {
        ref: rightSideContainerRef,
        children: /*#__PURE__*/ _jsx(BannerButtonList, {})
    }, "rightSide");
    return /*#__PURE__*/ _jsxs(Container, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                children: individualPrivacyOpen ? [
                    rightSideContainer,
                    leftSideContainer
                ] : [
                    leftSideContainer,
                    rightSideContainer
                ]
            }),
            /*#__PURE__*/ _jsx(BeforeFooter, {})
        ]
    });
};
export { BannerBody, BANNER_GROUP_NON_STANDARD_STYLE };
